import { useEffect, useRef, useState } from "react";
import { Registerer, SessionState, UserAgent } from "sip.js";
import { Card, CardContent, Stack, Typography, Avatar } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import ButtonOne from "../../common/normal-button";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";

// NOTE: this integration hasn't been runtime-tested against a live Asterisk
// WSS transport yet — that requires the DNS/TLS/pjsip.conf work described in
// WHATSAPP_VOICE_HANDOFF.md §Phase C, which isn't done. The SIP.js calls
// below follow that library's documented UserAgent/Registerer/Invitation
// pattern; re-verify against the installed sip.js version's docs the first
// time this actually registers against a real server.

const STATUS = {
  CONNECTING: "connecting",
  READY: "ready",
  RINGING: "ringing",
  IN_CALL: "in_call",
  ERROR: "error",
};

const formatDuration = (totalSeconds) => {
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const Softphone = ({ credentials }) => {
  const { websocket_url: websocketUrl, sip_domain: sipDomain, username, password } = credentials;

  const [status, setStatus] = useState(STATUS.CONNECTING);
  const [errorMessage, setErrorMessage] = useState("");
  const [caller, setCaller] = useState("");
  const [muted, setMuted] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);

  const userAgentRef = useRef(null);
  const registererRef = useRef(null);
  const activeSessionRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const startTimer = () => {
    setCallSeconds(0);
    timerRef.current = setInterval(() => setCallSeconds((s) => s + 1), 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
    setCallSeconds(0);
  };

  const attachRemoteAudio = (session) => {
    const peerConnection = session.sessionDescriptionHandler?.peerConnection;
    if (!peerConnection || !audioRef.current) return;

    const remoteStream = new MediaStream();
    peerConnection.getReceivers().forEach((receiver) => {
      if (receiver.track) remoteStream.addTrack(receiver.track);
    });
    peerConnection.addEventListener("track", (event) => {
      remoteStream.addTrack(event.track);
    });

    audioRef.current.srcObject = remoteStream;
    audioRef.current.play().catch(() => {});
  };

  const handleIncomingCall = (invitation) => {
    if (activeSessionRef.current) {
      // Already on a call — reject the new one rather than juggling two.
      invitation.reject().catch(() => {});
      return;
    }

    activeSessionRef.current = invitation;
    setCaller(invitation.remoteIdentity?.uri?.user || "Unknown caller");
    setStatus(STATUS.RINGING);

    invitation.stateChange.addListener((newState) => {
      if (newState === SessionState.Established) {
        attachRemoteAudio(invitation);
        setStatus(STATUS.IN_CALL);
        startTimer();
      } else if (newState === SessionState.Terminated) {
        activeSessionRef.current = null;
        stopTimer();
        setStatus(STATUS.READY);
        setCaller("");
      }
    });
  };

  useEffect(() => {
    let disposed = false;

    const uri = UserAgent.makeURI(`sip:${username}@${sipDomain}`);
    if (!uri) {
      setStatus(STATUS.ERROR);
      setErrorMessage("Invalid SIP address — check the tenant's voice configuration.");
      return undefined;
    }

    const userAgent = new UserAgent({
      uri,
      authorizationUsername: username,
      authorizationPassword: password,
      transportOptions: { server: websocketUrl },
      delegate: { onInvite: handleIncomingCall },
    });
    userAgentRef.current = userAgent;

    const start = async () => {
      try {
        await userAgent.start();
        if (disposed) return;

        const registerer = new Registerer(userAgent);
        registererRef.current = registerer;
        await registerer.register();

        if (!disposed) setStatus(STATUS.READY);
      } catch (err) {
        console.error("Softphone failed to start:", err);
        if (!disposed) {
          setStatus(STATUS.ERROR);
          setErrorMessage("Could not connect to the calling server.");
        }
      }
    };

    start();

    return () => {
      disposed = true;
      clearInterval(timerRef.current);
      registererRef.current?.unregister().catch(() => {});
      userAgentRef.current?.stop().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websocketUrl, sipDomain, username, password]);

  const answerCall = () => {
    activeSessionRef.current
      ?.accept({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } } })
      .catch((err) => console.error("Failed to answer call:", err));
  };

  const rejectCall = () => {
    activeSessionRef.current?.reject().catch(() => {});
  };

  const hangUp = () => {
    const session = activeSessionRef.current;
    if (!session) return;

    if (session.state === SessionState.Established) {
      session.bye().catch(() => {});
    } else {
      session.reject().catch(() => {});
    }
  };

  const toggleMute = () => {
    const peerConnection = activeSessionRef.current?.sessionDescriptionHandler?.peerConnection;
    if (!peerConnection) return;

    const nextMuted = !muted;
    peerConnection.getSenders().forEach((sender) => {
      if (sender.track?.kind === "audio") sender.track.enabled = !nextMuted;
    });
    setMuted(nextMuted);
  };

  return (
    <Card sx={{ maxWidth: 420, borderRadius: "12px" }}>
      <CardContent>
        <audio ref={audioRef} autoPlay hidden />

        <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: ColorsConstants.primaryBlue100 }}>
            <CallIcon fontSize="large" />
          </Avatar>

          {status === STATUS.CONNECTING && (
            <Typography sx={typography.bodyLarge}>Connecting to the calling server…</Typography>
          )}

          {status === STATUS.ERROR && (
            <Typography sx={typography.bodyLarge} color={ColorsConstants.error}>
              {errorMessage}
            </Typography>
          )}

          {status === STATUS.READY && (
            <Typography sx={typography.bodyLarge} color={ColorsConstants.success}>
              Ready — waiting for calls
            </Typography>
          )}

          {status === STATUS.RINGING && (
            <>
              <Typography sx={typography.bodyLarge}>Incoming call from {caller}</Typography>
              <Stack direction="row" spacing={2}>
                <ButtonOne title="Answer" startIcon={<CallIcon />} onClick={answerCall} />
                <ButtonOne
                  title="Reject"
                  variant="outlined"
                  startIcon={<CallEndIcon />}
                  onClick={rejectCall}
                />
              </Stack>
            </>
          )}

          {status === STATUS.IN_CALL && (
            <>
              <Typography sx={typography.bodyLarge}>In call with {caller}</Typography>
              <Typography sx={typography.bodyLarge}>{formatDuration(callSeconds)}</Typography>
              <Stack direction="row" spacing={2}>
                <ButtonOne
                  title={muted ? "Unmute" : "Mute"}
                  variant="outlined"
                  startIcon={muted ? <MicOffIcon /> : <MicIcon />}
                  onClick={toggleMute}
                />
                <ButtonOne
                  title="Hang up"
                  startIcon={<CallEndIcon />}
                  onClick={hangUp}
                  sx={{
                    backgroundColor: ColorsConstants.error,
                    "&:hover": { backgroundColor: ColorsConstants.error },
                  }}
                />
              </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default Softphone;
